/**
 * feat-307-redesign-tenant-mgmt (spec D2) — the page header.
 *
 * Back · title · [Generate demo tenant] [New tenant] · identity · Logout.
 * The two create actions are Admin-only: the caller passes `canCreateTenant`
 * (the same `fallbackPermissions.canCreateTenant` develop's panels used).
 *
 * The identity block is compact. Develop printed four lines ("Logged in as:
 * email (role)", tenant id, location id, a role-scope sentence); the first line
 * stays visible and the rest moves into the block's tooltip.
 */
import React from 'react';
import { ArrowLeft, LogOut, PanelLeft, Plus, Sparkles } from './icons';

export const DEMO_SETUP_BLURB =
  "Auto-generate a complete tenant from a restaurant's website — includes prompt, menu items, playbook, and roleplay script.";

export interface ManagementHeaderProps {
  onBack: () => void;
  onLogout: () => void;
  canCreateTenant: boolean;
  onGenerateDemo: () => void;
  onNewTenant: () => void;
  email: string;
  role: string;
  tenantId?: string | null;
  locationId?: string | null;
  scopeSentence?: string | null;
  /** D10 — the narrow-viewport sidebar toggle; omitted when there is no sidebar. */
  sidebarToggle?: { open: boolean; onToggle: () => void } | null;
}

const btnSecondary =
  'inline-flex items-center gap-1.5 rounded-lg bg-white border border-softBorder text-ink px-3 py-1.5 text-sm font-bold hover:bg-cream-dark whitespace-nowrap';
const btnPrimary =
  'inline-flex items-center gap-1.5 rounded-lg bg-coral text-white px-3 py-1.5 text-sm font-bold hover:brightness-95 whitespace-nowrap';
const btnGhost =
  'inline-flex items-center gap-1.5 rounded-lg text-ink px-2 py-1 text-sm font-semibold hover:bg-cream-dark whitespace-nowrap';

export default function ManagementHeader({
  onBack,
  onLogout,
  canCreateTenant,
  onGenerateDemo,
  onNewTenant,
  email,
  role,
  tenantId,
  locationId,
  scopeSentence,
  sidebarToggle,
}: ManagementHeaderProps) {
  const identityTitle = [
    tenantId ? `Tenant: ${tenantId}` : null,
    locationId ? `Location: ${locationId}` : null,
    scopeSentence || null,
  ]
    .filter(Boolean)
    .join('\n');

  return (
    <header className="bg-white border-b border-softBorder" data-testid="tenant-management-header">
      <div className="px-4 lg:px-6 py-3 flex items-center gap-x-3 gap-y-2 flex-wrap">
        {sidebarToggle && (
          <button
            type="button"
            onClick={sidebarToggle.onToggle}
            aria-expanded={sidebarToggle.open}
            aria-controls="tenant-tree"
            className={`${btnGhost} border border-softBorder lg:hidden`}
            data-testid="tenant-tree-toggle"
          >
            <PanelLeft className="w-4 h-4" aria-hidden="true" />
            <span>Tenants</span>
          </button>
        )}
        <button type="button" onClick={onBack} className={`${btnGhost} text-inkMuted`} title="Go back to previous page">
          <ArrowLeft className="w-4 h-4" aria-hidden="true" />
          <span>Back</span>
        </button>
        <h1 className="font-display text-xl sm:text-2xl font-semibold text-ink">Multi-Tenant Management</h1>
        <div className="ml-auto flex items-center gap-2 flex-wrap justify-end">
          {canCreateTenant && (
            <>
              <button type="button" onClick={onGenerateDemo} className={btnSecondary} title={DEMO_SETUP_BLURB}>
                <Sparkles className="w-4 h-4" aria-hidden="true" />
                Generate demo tenant
              </button>
              <button type="button" onClick={onNewTenant} className={btnPrimary}>
                <Plus className="w-4 h-4" aria-hidden="true" />
                New tenant
              </button>
            </>
          )}
          <div
            className="text-right text-xs leading-tight pl-2 sm:border-l border-softBorder min-w-0"
            title={identityTitle || undefined}
            data-testid="identity-block"
          >
            <div className="text-ink font-semibold truncate max-w-[16rem]">
              <span className="sr-only">Logged in as: </span>
              {email}
            </div>
            <div className="text-inkMuted">{role}</div>
            {identityTitle && <span className="sr-only">{identityTitle}</span>}
          </div>
          <button type="button" onClick={onLogout} className={btnGhost}>
            <LogOut className="w-4 h-4" aria-hidden="true" />
            <span>Logout</span>
          </button>
        </div>
      </div>
    </header>
  );
}
